import { addDays } from 'date-fns/addDays';
import { differenceInBusinessDays } from 'date-fns/differenceInBusinessDays';
import { isBefore } from 'date-fns/isBefore';
import { isFuture } from 'date-fns/isFuture';
import { startOfDay } from 'date-fns/startOfDay';
import { subDays } from 'date-fns/subDays';

import { getZonedDate } from '@eika-infrastruktur/date';

import type { AccountNumber, PaymentItem, LoanPayment } from '../../generated-types';

const fakeFuturePayment = (detail: LoanPayment): PaymentItem & { isFaked: boolean } => {
    return {
        uniqueId: detail.accountId,
        permissions: {
            canApprove: false,
            canRestart: false,
            canUnapprove: false,
            canEdit: false,
            canReject: false,
            canView: true,
        },
        amount: detail.nextPaymentAmount,
        estimatedAmount: detail.nextPaymentAmount,
        paymentItemId: detail.accountId,
        currency: 'NOK',
        receiver: { accountNumber: detail.accountNumber },
        from: { accountNumber: detail.debitAccountNumber },
        dueDate: detail.nextPaymentDate,
        plannedExecutionDate: detail.nextPaymentDate,
        paymentItemType: 'PAYMENT',
        isFaked: true,
        note: 'Nedbetaling lån',
        paymentItemStatus: 'PENDING_PAYMENT',
    };
};

const filterDuplicatedLoan = (futurePayments: PaymentItem[]) => {
    return (detail: LoanPayment): boolean => {
        const loanDate = getZonedDate(detail.nextPaymentDate);

        return !futurePayments
            .filter((payment) => payment.paymentItemType === 'PAYMENT')
            .filter((payment) => !payment.permissions.canReject)
            .filter((payment) => payment.receiver?.accountNumber?.value === detail.accountNumber.value)
            .filter((payment) => payment.from?.accountNumber?.value === detail.debitAccountNumber.value)
            .some((payment) => differenceInBusinessDays(loanDate, getZonedDate(payment.dueDate)) < 2);
    };
};

const getAccountSpecificLoans = (loanPayments: LoanPayment[], forAccount: AccountNumber | undefined): LoanPayment[] => {
    if (!forAccount) return loanPayments;
    return loanPayments.filter((loan) => loan.debitAccountNumber && loan.debitAccountNumber.value === forAccount.value);
};

export const extendWithFakedLoans = (
    futurePayments: PaymentItem[],
    loanPayments: LoanPayment[],
    forAccount?: AccountNumber,
): PaymentItem[] => {
    const validLoanDetails: LoanPayment[] = getAccountSpecificLoans(loanPayments, forAccount)
        .filter((loan) => loan.nextPaymentDate && isFuture(subDays(new Date(loan.nextPaymentDate), 2)))
        .filter(filterDuplicatedLoan(futurePayments));

    return [...futurePayments, ...validLoanDetails.map((detail) => fakeFuturePayment(detail))].sort(
        (a: PaymentItem, b: PaymentItem) => new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime(),
    );
};

export const filterNextMonthPayments = (futurePayments: PaymentItem[]): PaymentItem[] => {
    const nextMonthStartOfDay = startOfDay(addDays(new Date(), 31));

    const transferTypes = ['TRANSFER', 'RECURRING_TRANSFER'];

    return futurePayments
        .filter((payment) => !transferTypes.includes(payment.paymentItemType ?? 'UNKNOWN'))
        .filter((payment) => isBefore(new Date(payment.dueDate), nextMonthStartOfDay));
};

export const getTotalResult = (futurePayments: PaymentItem[]) => {
    return {
        numberOfFuturePayments: futurePayments.length,
        totalAmount: futurePayments.reduce(
            (total, payment) => total + Math.abs(payment.estimatedAmount || payment.amount),
            0,
        ),
    };
};
